/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import dayjs, { Dayjs, ManipulateType, OpUnitType, QUnitType } from 'dayjs';
import calendar from 'dayjs/plugin/calendar';
import utc from 'dayjs/plugin/utc';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { DataPeriods } from '../types/universalTracker';
import { getPeriodName } from './universalTracker';
import { ReactSelectOptions } from '../components/survey/form/input/selectUtils';

dayjs.extend(utc)
dayjs.extend(duration)
dayjs.extend(calendar)
dayjs.extend(relativeTime)
dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat)

interface SetDateProps {
  year?: number | string,
  month?: number | string
}

type DateString = string | Date;
type CommonDate = DateString | Dayjs;

type EndOfUnit = 'hour' | 'day' | 'month' | 'year' | 'date'
interface SetTimeProps {
  hour: number;
  minute: number;
  second: number;
}


export const DATE = {
  DEFAULT_SPACES: 'DD MMM YYYY',
  DEFAULT_DASHES: 'DD-MMM-YYYY',
  MONTH_YEAR: 'MMMM YYYY',
  MONTH_YEAR_SHORT: 'MMM YYYY',
  MONTH: 'MMM',
  LONG: 'Do [of] MMMM, YYYY',
  SORTABLE: 'YYYY-MM-DD HH:mm:ss',
  HUMANIZE: 'humanize',
  DEFAULT_SPACES_WITH_TIME: 'DD MMM YYYY, HH:mm:ss',
  UK: 'DD/MM/YYYY',
  YEAR_MONTH: 'YYYY MMMM',
  DATE_PICKER: 'YYYY-MM-DD',
  MONTH_ONLY: 'MMMM',
  YEAR_ONLY: 'YYYY',
  FILE_NAME: 'YYYY-MM-DD_HH-mm-ss',
  TOOLTIP: 'MMM D, YYYY',
  ISO: 'YYYY-MM-DDTHH:mm:ss.sssZ',
};

const getNextYears = (numberOfNextYears: number, currentMonth: number) => {
  if (numberOfNextYears === 0) {
    return currentMonth >= 11 ? 1 : 0;
  }
  return numberOfNextYears;
}

export const getYearDropdownOptions = (numberOfYears: number = 5, numberOfNextYears: number = 0) => {
  const currentMonth = dayjs().month();
  const currentYear = dayjs().year();
  const endYear = currentYear + getNextYears(numberOfNextYears, currentMonth);
  const startYear = currentYear - numberOfYears;
  const options: ReactSelectOptions[] = [];
  for (let year = endYear; year > startYear; year--) {
    options.push({ value: year, label: year });
  }

  return options;
}

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
export const MONTHS_VALUE = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

const validDateFormats = [DATE.UK, DATE.DATE_PICKER];

export const parseUserStringToDate = (date: string) => {
  return dayjs(date, validDateFormats, true);
};

/** @deprecated will be replaced with formatDateNonUTC & formatDateUTC */
export function formatDate(date: string | Date, format = DATE.DEFAULT_SPACES, utc = false) {
  if (!date) {
    return '';
  }
  const day = utc ? dayjs.utc(date) : dayjs(date);
  switch (format) {
    case DATE.HUMANIZE:
      return day.fromNow();
    default:
      return day.format(format);
  }
}

export const formatDateNonUTC = (date: string | Date, format = DATE.DEFAULT_SPACES) => formatDate(date, format, false);

export const formatDateUTC = (date: string | Date, format = DATE.DEFAULT_SPACES) => formatDate(date, format, true);

export function formatLongDate(date?: string | Date) {
  return date ? dayjs(date).format('DD/MM/YYYY HH:mm:ss') : '';
}

export const getMonthName = (monthNum: number) =>
  dayjs
    .utc()
    .set('month', monthNum - 1)
    .format(DATE.MONTH_ONLY);

type DateActions = 'add' | 'sub' | '';

export const DEFAULT_API_PAST_MONTHS = 3;

export function formatDatePickerString(
  value: number = 0,
  type: DateActions = 'add',
  format: string = DATE.DATE_PICKER,
  unit: ManipulateType = 'month',
  utc = false
) {
  let date = utc ? dayjs.utc() : dayjs();
  switch (type) {
    case 'add':
      return date.add(value, unit).format(format);
    case 'sub':
      return date.subtract(value, unit).format(format);
    default:
      return date.format(format);
  }
}

export const getPeriodOptions = () => ([
  {
    value: DataPeriods.Monthly,
    label: getPeriodName(DataPeriods.Monthly)
  },
  {
    value: DataPeriods.Quarterly,
    label: getPeriodName(DataPeriods.Quarterly)
  },
  {
    value: DataPeriods.Yearly,
    label: getPeriodName(DataPeriods.Yearly)
  }
]);

export const getMonthOptions = () => months.map((m, i) => ({
  value: i,
  label: m
}))

export const getMonthNonJSOptions = () => months.map((m, i) => ({
  value: i + 1,
  label: m
}))

export const getDuration = (diff: number) => {
  return dayjs.duration(diff)
}

export const getDiff = (date1: DateString, date2: string | Date = new Date(), unit?: QUnitType | OpUnitType) => {
  return dayjs(date1).diff(date2, unit);
}

export const getCalendarDate = (date?: DateString) => {
  return dayjs(date).calendar();
}

export const getYear = (date?: DateString, utc = false) => {
  return (utc ? dayjs.utc(date) : dayjs(date)).year();
}

export const getMonth = (date?: DateString) => {
  return dayjs(date).month();
}

export const getFullUTCDate = (year: number, month: number, day: number, date?: DateString): Date => {
  return dayjs.utc(date).year(Number(year)).month(Number(month)).date(Number(day)).toDate()
}

export const getUTCStartOf = (unit: EndOfUnit, date?: DateString,) => {
  return dayjs.utc(date).startOf(unit).toDate()
}

export const getUTCEndOf = (unit: EndOfUnit, date?: DateString) => {
  return dayjs.utc(date).endOf(unit).toDate();
}

export const setFormat = (format: string, date?: DateString) => {
  return dayjs(date).format(format)
}

export const setToISOString = (date?: DateString) => {
  return dayjs(date).toISOString();
}

export const getCurrentStartOfTheDayDate = (value: EndOfUnit, date?: DateString) => {
  return dayjs.utc(date).startOf(value).toDate();
}

export const setDate = ({ year, month }: SetDateProps, date?: DateString, utc = false) => {
  let effectiveDate = utc ? dayjs.utc(date) : dayjs(date);
  if (year) {
    effectiveDate = effectiveDate.set('year', Number(year))
  }
  if (undefined !== month) {
    effectiveDate = effectiveDate.set('month', Number(month))
  }

  return effectiveDate.toDate();
}

export const getDate = (date?: CommonDate) => dayjs(date);

export const getDateUnit = (unit: EndOfUnit, date?: DateString) => {
  return dayjs(date).get(unit);
}

export const getUtcDateUnit = (unit: EndOfUnit, date?: DateString) => {
  return dayjs.utc(date).get(unit);
}

export const getUtc = (value: string) => {
  return dayjs.utc(value)
}

export const subtractDate = (date: CommonDate, value: number, unit: ManipulateType, utc = true) => {
  if (utc) {
    return dayjs.utc(date).subtract(value, unit).toDate();
  }
  return dayjs(date).subtract(value, unit).toDate();
}

export const currentTimestamp = () => dayjs().unix()

export const isBefore = (value: CommonDate | number, date?: CommonDate) => {
  return dayjs(date).isBefore(value);
};

export const isAfter = (value?: CommonDate | number, date?: CommonDate) => {
  return dayjs(date).isAfter(value);
};

export const isSame = (date: CommonDate | undefined, compareDate: CommonDate | undefined, unit?: ManipulateType) => {
  return dayjs(date).isSame(compareDate, unit);
}

export const getDateTime = (date: DateString, time?: SetTimeProps) => {
  const current = dayjs();
  const dateTime = dayjs(date);

  if (time) {
    return dateTime.set('hour', time.hour).set('minute', time.minute).set('second', time.second).toDate();
  }
  return dateTime.set('hour', current.hour()).set('minute', current.minute()).set('second', current.second()).toDate();
};

export const getFutureDate = (numOfDays: number, unit: ManipulateType = 'day') => {
  return dayjs().add(numOfDays, unit).toDate();
}

export const getFutureIsoString = (numOfDays: number = 0, unit: ManipulateType = 'day') => {
  return dayjs().add(numOfDays, unit).toISOString();
}

export const TIME_RANGE_OPTIONS = [
  { code: 12, name: 'Last 12 months' },
  { code: 6, name: 'Last 6 months' },
  { code: 3, name: 'Last 3 months' },
  { code: 1, name: 'Last 1 month' },
  { code: 0, name: 'All time' },
  { code: 'custom', name: 'Custom dates', showRange: true },
];

export const sortEffectiveDateDesc = (a: string, b: string) => (dayjs.utc(a).isBefore(dayjs.utc(b)) ? 1 : -1);

export const isSameOrBeforeDate = (date: CommonDate, compareDate: CommonDate) => {
  return isSame(date, compareDate) || isBefore(date, compareDate);
};
